// Hardened host + mount controller (see WATERMARK_PORTABLE_BUILD_GUIDE §4.2). Framework-agnostic.
import type { WatermarkConfig } from './watermark-config';
import type { WatermarkAdapter } from './watermark-adapter';
import { buildTileUrl, resolveWatermarkText, shortHash, type WatermarkTokens } from './watermark-tile';

const HOST_CSS = 'position:fixed;inset:0;pointer-events:none;z-index:2147483646;';
const ROTATE_MS = 30_000;
const TAMPER_THRESHOLD = 3;
const BEACON_THROTTLE_MS = 10_000;
const FORCE_LOGOUT_THRESHOLD = 6;
const PENALTY_BLUR_MS = 1_200;

/** Dense, near-invisible tiling for the forensic layer. Black + `multiply` → recoverable on
 *  LIGHT backgrounds (multiply can only darken). */
const FORENSIC_CFG: WatermarkConfig = {
  enabled: true, apps: [], parts: [], customText: '', separator: '',
  color: '#000000', opacity: 0.008, fontSize: 11, fontWeight: 600, rotation: -30, gap: 150, scatter: false,
  forensic: false, selfHeal: false, timestampRotate: false, tamperTelemetry: false, penaltyBlur: false, forceLogout: false,
};

/** Complementary forensic layer: near-white + `screen` → recoverable on DARK backgrounds (screen can
 *  only brighten). Same geometry/opacity as FORENSIC_CFG so both grids overlay exactly and carry the
 *  identical hash; on any given background only the matching layer leaves a recoverable trace. */
const FORENSIC_CFG_LIGHT: WatermarkConfig = { ...FORENSIC_CFG, color: '#ffffff' };

const rand = () => Math.random().toString(36).slice(2, 8);

interface Paint { config: WatermarkConfig; tokens: WatermarkTokens; sidebarWidth: number; headerOffset: number }

export class WatermarkHost {
  private host: HTMLElement | null = null;
  private visible: HTMLDivElement | null = null;
  private forensic: HTMLDivElement | null = null;
  private forensicLight: HTMLDivElement | null = null;
  private observer: MutationObserver | null = null;
  private timer: number | null = null;
  private tampers = 0;
  private lastBeacon = 0;
  private loggedOut = false;
  private readonly hostId = '_' + rand();
  private readonly tag = 'x-' + rand();
  private paint: Paint | null = null;

  constructor(private adapter: WatermarkAdapter) {}

  private mount(): void {
    if (this.host) return;
    const host = document.createElement(this.tag);
    host.id = this.hostId;
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = HOST_CSS;
    const root = host.attachShadow({ mode: 'closed' });
    const style = document.createElement('style');
    const off = this.paint?.headerOffset ?? 0;
    style.textContent =
      `.wm{position:absolute;left:0;top:${off}px;right:0;bottom:0;pointer-events:none;background-repeat:repeat;}` +
      `@media (min-width:1024px){.wm{left:var(--wm-left,0px);}}` +
      `.wm-forensic{position:absolute;inset:0;pointer-events:none;background-repeat:repeat;mix-blend-mode:multiply;}` +
      `.wm-forensic-light{position:absolute;inset:0;pointer-events:none;background-repeat:repeat;mix-blend-mode:screen;}`;
    const forensic = document.createElement('div'); forensic.className = 'wm-forensic';
    const forensicLight = document.createElement('div'); forensicLight.className = 'wm-forensic-light';
    const visible = document.createElement('div'); visible.className = 'wm';
    root.append(style, forensic, forensicLight, visible);
    document.body.appendChild(host);
    this.host = host; this.visible = visible; this.forensic = forensic; this.forensicLight = forensicLight;
    this.repaint();
  }

  private ensureObserver(): void {
    const want = !!this.host && this.paint?.config.selfHeal !== false;
    if (want && !this.observer) this.startObserver();
    else if (!want && this.observer) { this.observer.disconnect(); this.observer = null; }
  }

  private startObserver(): void {
    this.observer = new MutationObserver(() => {
      if (!this.host) return;
      let tampered = false;
      if (!this.host.isConnected) { document.body.appendChild(this.host); tampered = true; }
      const s = this.host.style;
      if (s.display === 'none' || s.visibility === 'hidden' || (s.opacity !== '' && Number(s.opacity) < 0.5)) {
        this.applyHostStyle(); tampered = true;
      }
      if (tampered) {
        this.tampers += 1;
        if (this.paint?.config.penaltyBlur) this.penaltyBlur();
        if (this.tampers >= TAMPER_THRESHOLD) this.reportTamper();
        if (this.paint?.config.forceLogout && this.tampers >= FORCE_LOGOUT_THRESHOLD && !this.loggedOut) {
          this.loggedOut = true;
          try { this.adapter.onForceLogout?.(); } catch { /* best-effort */ }
        }
      }
    });
    this.observer.observe(document.body, { childList: true });
    this.observer.observe(this.host!, { attributes: true, attributeFilter: ['style', 'class', 'id'] });
  }

  private reportTamper(): void {
    if (!this.paint || this.paint.config.tamperTelemetry === false) return;
    const now = Date.now();
    if (now - this.lastBeacon < BEACON_THROTTLE_MS) return;
    this.lastBeacon = now;
    const { tokens } = this.paint;
    const fingerprint = tokens.employeeId && tokens.sessionId ? shortHash(`${tokens.employeeId}:${tokens.sessionId}`) : '';
    try {
      const url = `${this.adapter.apiBaseUrl}${this.adapter.tamperPath ?? '/api/track/watermark-tamper'}`;
      void fetch(url, {
        method: 'POST', keepalive: true, credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(this.adapter.getAuthHeaders?.() ?? {}) },
        body: JSON.stringify({ sessionId: tokens.sessionId ?? '', fingerprint, route: location.pathname, count: this.tampers }),
      }).catch(() => {});
    } catch { /* best-effort */ }
  }

  private penaltyBlur(): void {
    const sel = this.adapter.getContentSelector?.() ?? '[data-app-content]';
    const el = document.querySelector(sel) as HTMLElement | null;
    if (!el) return;
    el.style.transition = 'filter .15s ease';
    el.style.filter = 'blur(8px)';
    window.setTimeout(() => { el.style.filter = ''; el.style.transition = ''; }, PENALTY_BLUR_MS);
  }

  private applyHostStyle(): void {
    if (!this.host) return;
    this.host.style.cssText = HOST_CSS;
    if (this.paint) this.host.style.setProperty('--wm-left', this.paint.sidebarWidth + 'px');
  }

  private paintVisible(): void {
    if (!this.visible || !this.paint) return;
    const text = resolveWatermarkText(this.paint.config, this.paint.tokens);
    const seed = this.paint.tokens.sessionId || this.paint.tokens.employeeId;
    this.visible.style.backgroundImage = text ? buildTileUrl(text, this.paint.config, seed) : 'none';
  }

  private paintForensic(): void {
    if (!this.forensic || !this.forensicLight || !this.paint) return;
    const { config, tokens } = this.paint;
    const on = config.forensic !== false && !!tokens.employeeId && !!tokens.sessionId;
    // Same hash on both layers: black/multiply attributes light backgrounds, white/screen attributes
    // dark backgrounds. Whichever matches the surface leaves a recoverable trace; the other is inert.
    const fp = on ? shortHash(`${tokens.employeeId}:${tokens.sessionId}`) : '';
    this.forensic.style.backgroundImage = on ? buildTileUrl(fp, FORENSIC_CFG) : 'none';
    this.forensicLight.style.backgroundImage = on ? buildTileUrl(fp, FORENSIC_CFG_LIGHT) : 'none';
  }

  private ensureTimer(): void {
    const rotate = !!this.paint && this.paint.config.timestampRotate !== false && this.paint.config.parts.includes('timestamp');
    if (rotate && this.timer == null) this.timer = window.setInterval(() => this.paintVisible(), ROTATE_MS);
    else if (!rotate && this.timer != null) { clearInterval(this.timer); this.timer = null; }
  }

  private repaint(): void { this.applyHostStyle(); this.paintVisible(); this.paintForensic(); this.ensureTimer(); this.ensureObserver(); }

  set(paint: Paint): void { this.paint = paint; if (!this.host) this.mount(); else this.repaint(); }

  destroy(): void {
    if (this.timer != null) { clearInterval(this.timer); this.timer = null; }
    this.observer?.disconnect(); this.observer = null;
    this.host?.remove(); this.host = this.visible = this.forensic = this.forensicLight = null;
    this.paint = null; this.tampers = 0; this.lastBeacon = 0; this.loggedOut = false;
  }
}

/** Framework-free controller. Call `update()` whenever config, user, or route changes. */
export function mountScreenWatermark(adapter: WatermarkAdapter) {
  const host = new WatermarkHost(adapter);
  const update = () => {
    const config = adapter.getConfig();
    const tokens = adapter.getTokens();
    const routeOk = adapter.isRouteAllowed ? adapter.isRouteAllowed(location.pathname) : true;
    const show = !!config?.enabled && !!tokens && routeOk;
    if (show) host.set({ config: config!, tokens: tokens!, sidebarWidth: adapter.getSidebarWidth?.() ?? 0, headerOffset: adapter.getHeaderOffset?.() ?? 0 });
    else host.destroy();
  };
  update();
  window.addEventListener('popstate', update);
  return { update, destroy: () => { window.removeEventListener('popstate', update); host.destroy(); } };
}
